// Tema claro/oscuro del panel y sus colores. El negocio los elige en Ajustes ›
// Configuración › Apariencia (config `tema`, `color_primario`, `color_primario_oscuro`,
// `fondo_claro`, `fondo_oscuro`); se guardan también en localStorage para que el
// login y el primer pintado ya salgan correctos, sin parpadeo.
const KEY = 'chargoy.tema';
const KEY_COLORES = 'chargoy.colores';

export type Tema = 'claro' | 'oscuro';
export type Colores = { marcaClaro?: string; marcaOscuro?: string; fondoClaro?: string; fondoOscuro?: string };

export const coloresDeConfig = (c: Record<string, string | undefined>): Colores => ({
  marcaClaro: c.color_primario || undefined, marcaOscuro: c.color_primario_oscuro || undefined,
  fondoClaro: c.fondo_claro || undefined, fondoOscuro: c.fondo_oscuro || undefined,
});

const HEX = /^#[0-9a-f]{6}$/i;
export const esHex = (v?: string) => !!v && HEX.test(v);

// Luminancia relativa (WCAG) de un #rrggbb.
export function luminancia(hex: string) {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map(v => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export const contraste = (a: string, b: string) => {
  const [x, y] = [luminancia(a), luminancia(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
};

// Superficies y líneas a partir del fondo, con las mismas proporciones que la
// paleta de fábrica (index.css): en claro se oscurece un poco, en oscuro se aclara.
export function paletaDeFondo(fondo: string, oscuro: boolean): Record<string, string> {
  const mix = (hacia: string, pct: number) => `color-mix(in oklab, ${fondo}, ${hacia} ${pct}%)`;
  return oscuro
    ? { '--bg': fondo, '--surface': mix('white', 4), '--line-2': mix('white', 9), '--well': mix('white', 10), '--line': mix('white', 14), '--line-strong': mix('white', 22) }
    : { '--bg': fondo, '--surface': mix('white', 70), '--line-2': mix('black', 2.5), '--well': mix('black', 4), '--line': mix('black', 7), '--line-strong': mix('black', 14) };
}
const VARS_FONDO = ['--bg', '--surface', '--line-2', '--well', '--line', '--line-strong'];

function leerColores(): Colores {
  try { return JSON.parse(localStorage.getItem(KEY_COLORES) || '{}'); } catch { return {}; }
}

export function aplicarTema(tema: string = localStorage.getItem(KEY) || 'claro', colores?: Colores) {
  const t: Tema = tema === 'oscuro' ? 'oscuro' : 'claro';
  const oscuro = t === 'oscuro';
  const c = colores ?? leerColores();
  const root = document.documentElement;
  root.dataset.theme = t;
  localStorage.setItem(KEY, t);
  if (colores) localStorage.setItem(KEY_COLORES, JSON.stringify(colores));

  // Marca: en oscuro la suya propia; si no hay, la de claro. Sin ninguna, la de index.css.
  const marca = oscuro ? (c.marcaOscuro || c.marcaClaro) : c.marcaClaro;
  if (esHex(marca)) {
    root.style.setProperty('--brand', marca!);
    // Texto sobre botones de marca: blanco u oscuro, el que se lea mejor.
    root.style.setProperty('--on-brand', contraste(marca!, '#ffffff') >= contraste(marca!, '#1B1713') ? '#ffffff' : '#1B1713');
    // Marca como texto: si la elegida ya es clara en oscuro se usa tal cual; si no, index.css la aclara.
    if (oscuro && c.marcaOscuro && luminancia(marca!) > 0.3) root.style.setProperty('--brand-texto', marca!);
    else root.style.removeProperty('--brand-texto');
  } else ['--brand', '--on-brand', '--brand-texto'].forEach(v => root.style.removeProperty(v));

  const fondo = oscuro ? c.fondoOscuro : c.fondoClaro;
  if (esHex(fondo)) Object.entries(paletaDeFondo(fondo!, oscuro)).forEach(([k, v]) => root.style.setProperty(k, v));
  else VARS_FONDO.forEach(v => root.style.removeProperty(v));
}
